/**
 * TestPageView - Pure UI component for testing page
 *
 * This is the View layer that handles only presentation.
 * Contains no logic, hooks, or side effects.
 */
import type { FormEvent } from 'react';
import type { TestResult } from './testing.schema';
import type { PresetEndpoint } from './TestPage';
import './TestPageView.css';

interface TestPageViewProps {
  userId: string;
  onUserIdChange: (userId: string) => void;
  endpoint: string;
  onEndpointChange: (value: string) => void;
  method: string;
  onMethodChange: (value: string) => void;
  headers: string;
  onHeadersChange: (value: string) => void;
  body: string;
  onBodyChange: (value: string) => void;
  testResult: TestResult | null;
  loading: boolean;
  onFormSubmit: (event: FormEvent<HTMLFormElement>) => void;
  presets: PresetEndpoint[];
  onPresetSelect: (preset: PresetEndpoint) => void;
  userIdError?: string;
  endpointError?: string;
  headersError?: string;
  bodyError?: string;
}

// Derive unique optgroup labels in order from the preset list
function getGroups(presets: PresetEndpoint[]): string[] {
  const seen = new Set<string>();
  const groups: string[] = [];
  for (const p of presets) {
    if (!seen.has(p.group)) { seen.add(p.group); groups.push(p.group); }
  }
  return groups;
}

function TestPageView({
  userId,
  onUserIdChange,
  endpoint,
  onEndpointChange,
  method,
  onMethodChange,
  headers,
  onHeadersChange,
  body,
  onBodyChange,
  testResult,
  loading,
  onFormSubmit,
  presets,
  onPresetSelect,
  userIdError,
  endpointError,
  headersError,
  bodyError,
}: TestPageViewProps) {
  const userIdInputId      = 'testpageview-user-id';
  const presetSelectId     = 'testpageview-preset';
  const endpointInputId    = 'testpageview-endpoint';
  const methodSelectId     = 'testpageview-method';
  const headersTextareaId  = 'testpageview-headers';
  const bodyTextareaId     = 'testpageview-body';

  const groups = getGroups(presets);

  // Handle native select change → look up and forward the full preset object
  const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const idx = Number(e.target.value);
    if (!isNaN(idx) && presets[idx]) onPresetSelect(presets[idx]); // forward full preset so container can apply all fields
  };

  return (
    <main className="test-page-view-container">
      <h1 className="test-page-view-title">API Testing Interface</h1>

      <div className="test-page-view-form">
        <h2 className="test-page-view-subtitle">Test Configuration</h2>

        {/* ── User ID ─────────────────────────────────────────── */}
        <div className="test-page-view-form-group">
          <label htmlFor={userIdInputId} className="test-page-view-label">
            User ID:
          </label>
          <input
            id={userIdInputId}
            type="text"
            value={userId}
            onChange={(e) => onUserIdChange(e.target.value)}
            className="test-page-view-input"
            placeholder="Enter user ID"
            required
            aria-describedby={`${userIdError ? `${userIdInputId}-error ` : ''}userId-help`}
            aria-invalid={!!userIdError}
          />
          {userIdError && (
            <div id={`${userIdInputId}-error`} className="test-page-view-error" role="alert" aria-live="polite">
              {userIdError}
            </div>
          )}
          <div id="userId-help" className="test-page-view-help-text">
            Used to pre-fill USER_ID placeholders when loading a preset
          </div>
        </div>

        {/* ── Preset selector ─────────────────────────────────── */}
        <div className="test-page-view-form-group">
          <label htmlFor={presetSelectId} className="test-page-view-label">
            Load Preset:
          </label>
          <select
            id={presetSelectId}
            defaultValue=""
            onChange={handlePresetChange}
            className="test-page-view-input"
          >
            <option value="" disabled>— select an endpoint —</option>
            {groups.map((group) => (
              <optgroup key={group} label={group}>
                {presets
                  .map((p, i) => ({ p, i }))
                  .filter(({ p }) => p.group === group)
                  .map(({ p, i }) => (
                    <option key={i} value={i}>
                      {p.method.padEnd(6)} {p.label}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
          <div className="test-page-view-help-text">
            Selecting a preset fills the endpoint, method, and a sample body. Edit freely before submitting.
          </div>
        </div>

        <form onSubmit={onFormSubmit}>
          {/* ── Endpoint ──────────────────────────────────────── */}
          <div className="test-page-view-form-group">
            <label htmlFor={endpointInputId} className="test-page-view-label">
              Endpoint:
            </label>
            <input
              id={endpointInputId}
              type="text"
              value={endpoint}
              onChange={(e) => onEndpointChange(e.target.value)}
              className="test-page-view-input"
              placeholder="/api/endpoint"
              required
              pattern="^/.*"
              aria-describedby={`${endpointError ? `${endpointInputId}-error ` : ''}endpoint-help`}
              aria-invalid={!!endpointError}
            />
            {endpointError && (
              <div id={`${endpointInputId}-error`} className="test-page-view-error" role="alert" aria-live="polite">
                {endpointError}
              </div>
            )}
            <div id="endpoint-help" className="test-page-view-help-text">
              API endpoint path (must start with /)
            </div>
          </div>

          {/* ── Method ────────────────────────────────────────── */}
          <div className="test-page-view-form-group">
            <label htmlFor={methodSelectId} className="test-page-view-label">
              Method:
            </label>
            <select
              id={methodSelectId}
              value={method}
              onChange={(e) => onMethodChange(e.target.value)}
              className="test-page-view-input"
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>

          {/* ── Headers ───────────────────────────────────────── */}
          <div className="test-page-view-form-group">
            <label htmlFor={headersTextareaId} className="test-page-view-label">
              Headers (JSON):
            </label>
            <textarea
              id={headersTextareaId}
              value={headers}
              onChange={(e) => onHeadersChange(e.target.value)}
              className="test-page-view-input test-page-view-textarea test-page-view-textarea-headers"
              placeholder='{"X-API-Key": "your-api-key"}'
              aria-describedby={`${headersError ? `${headersTextareaId}-error ` : ''}${headersTextareaId}-help`}
              aria-invalid={!!headersError}
            />
            {headersError && (
              <div id={`${headersTextareaId}-error`} className="test-page-view-error" role="alert" aria-live="polite">
                {headersError}
              </div>
            )}
            <div id={`${headersTextareaId}-help`} className="test-page-view-help-text">
              All authenticated endpoints require {"\"X-API-Key\": \"your-key\""}
            </div>
          </div>

          {/* ── Body (hidden for GET) ─────────────────────────── */}
          {method !== 'GET' && (
            <div className="test-page-view-form-group">
              <label htmlFor={bodyTextareaId} className="test-page-view-label">
                Request Body (JSON):
              </label>
              <textarea
                id={bodyTextareaId}
                value={body}
                onChange={(e) => onBodyChange(e.target.value)}
                className="test-page-view-input test-page-view-textarea test-page-view-textarea-body"
                placeholder='{"key": "value"}'
                aria-describedby={`${bodyError ? `${bodyTextareaId}-error ` : ''}${bodyTextareaId}-help`}
                aria-invalid={!!bodyError}
              />
              {bodyError && (
                <div id={`${bodyTextareaId}-error`} className="test-page-view-error" role="alert" aria-live="polite">
                  {bodyError}
                </div>
              )}
              <div id={`${bodyTextareaId}-help`} className="test-page-view-help-text">
                Enter valid JSON request body
              </div>
            </div>
          )}

          <button type="submit" className="test-page-view-button" disabled={loading}>
            {loading ? 'Testing...' : 'Execute Test'}
          </button>
        </form>
      </div>

      {/* ── Result panel ────────────────────────────────────────── */}
      {testResult && (
        <div
          className={`test-page-view-result ${testResult.success ? 'test-page-view-result-success' : 'test-page-view-result-failure'}`}
          role={testResult.success ? 'status' : 'alert'}
          aria-live="polite"
        >
          <h3 className={`test-page-view-result-title ${testResult.success ? 'test-page-view-result-title-success' : 'test-page-view-result-title-failure'}`}>
            Test Result
          </h3>

          <p data-testid="test-result-status" className="test-page-view-result-status">
            <strong>Status:</strong> {testResult.status}
          </p>

          {testResult.error && (
            <div className="test-page-view-result-error">
              <strong>Error:</strong> {testResult.error}
            </div>
          )}

          <div>
            <strong>Response:</strong>
            <pre className={`test-page-view-result-response ${testResult.success ? 'test-page-view-result-response-success' : 'test-page-view-result-response-failure'}`}>
              {JSON.stringify(testResult.data, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </main>
  );
}

export default TestPageView;
